import { useState, type SubmitEvent } from 'react'
import { useTranslation } from 'react-i18next'

import { Alert, Button, toast } from '@/components/ui'
import { ApiError } from '@/api/problemDetails'

import { useCreateWebChatChannel } from '../queries'
import type { WebChatChannelCreated } from '../types'

interface CreateWebChatFormProps {
  readonly tenantId: string
  readonly onCreated: (created: WebChatChannelCreated) => void
}

/** Formulário de criação de widget: nome + origens autorizadas (uma por linha). Origens podem ser adicionadas depois. */
export function CreateWebChatForm({ tenantId, onCreated }: CreateWebChatFormProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [originsText, setOriginsText] = useState('')
  const mutation = useCreateWebChatChannel(tenantId)

  const submit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (name.trim().length === 0 || mutation.isPending) {
      return
    }
    mutation.mutate(
      { name: name.trim(), allowedOrigins: parseOrigins(originsText) },
      {
        onSuccess: (created) => {
          setName('')
          setOriginsText('')
          onCreated(created)
          toast.success(t('webchat.toast.created'))
          // O signing secret já foi içado para o pai (exibição única). Descartamos a cópia
          // residual no cache da mutação para que nada retenha o segredo após o dismiss.
          mutation.reset()
        },
      },
    )
  }

  return (
    <form
      onSubmit={submit}
      className="flex flex-col gap-4 rounded-lg border border-border-default bg-background-elevated p-5"
    >
      <div>
        <h2 className="text-sm font-semibold text-foreground-primary">
          {t('webchat.create.title')}
        </h2>
        <p className="text-sm text-foreground-muted">{t('webchat.create.subtitle')}</p>
      </div>

      {mutation.isError && (
        <Alert variant="danger" title={t('webchat.create.errorTitle')}>
          {mutation.error instanceof ApiError
            ? mutation.error.problem.title
            : t('webchat.create.errorGeneric')}
        </Alert>
      )}

      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-foreground-secondary">
          {t('webchat.create.nameLabel')}
        </span>
        <input
          value={name}
          onChange={(e) => {
            setName(e.target.value)
          }}
          placeholder={t('webchat.create.namePlaceholder')}
          maxLength={120}
          className="rounded-md border border-border-default bg-background-default px-3 py-2 text-sm text-foreground-primary outline-none focus:border-action-primary"
        />
      </label>

      <label className="flex flex-col gap-1.5">
        <span className="text-xs font-medium text-foreground-secondary">
          {t('webchat.create.originsLabel')}
        </span>
        <textarea
          value={originsText}
          onChange={(e) => {
            setOriginsText(e.target.value)
          }}
          placeholder={'https://app.seucliente.com'}
          rows={3}
          className="rounded-md border border-border-default bg-background-default px-3 py-2 font-mono text-xs text-foreground-primary outline-none focus:border-action-primary"
        />
        <span className="text-xs text-foreground-muted">{t('webchat.create.originsHint')}</span>
      </label>

      <div className="flex justify-end">
        <Button type="submit" disabled={name.trim().length === 0 || mutation.isPending}>
          {mutation.isPending ? t('webchat.create.submitting') : t('webchat.create.submit')}
        </Button>
      </div>
    </form>
  )
}

function parseOrigins(text: string): readonly string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
}
